import Axios from 'axios';
import moment from 'moment-timezone';
import queryString from 'query-string';
import {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { Menu } from '../../api/menu.api';
import { getUpsell, UpsellAPINS } from '../../api/upsell.api';
import Button from '../../components/common/Button';
import HeaderOLD from '../../components/OLD_Header';
import LoadingPage from '../../components/LoadingPage';
import { usePrevious } from '../../hooks';
import { BookingProcessStoreContext } from '../../stores/BookingProcessStore';

import URLBuilder from '../../utils/URLBuilder';
import { currencies } from '../../utils/currencyUtils';
import { useHistory } from 'react-router';
import { MenuCard } from './MenuCard.tsx';
import { trackDineoutDiscovery } from '@dineouticeland/do-tracking';

const BG = '#f7f8fa';
const CARD = '#ffffff';
const INK = '#1c1c1c';
const MUTED = '#9b9b9b';
const LINE = '#e9eef3';

const Style = styled.div`
    background-color: ${BG};
    min-height: 100vh;
    color: ${INK};
    padding-bottom: 140px;

    .h-header {
        background-color: rgba(247, 248, 250, 0.72);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border-bottom: 1px solid rgba(28, 28, 28, 0.05);
    }

    .upsell-top {
        padding: 16px;
    }

    .upsell-header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 24px;
        padding-top: 88px;
        margin-bottom: 28px;

        h1 {
            font-size: 28px;
            font-weight: 600;
            letter-spacing: -0.02em;
            margin: 0;

            @media screen and (min-width: 768px) {
                font-size: 32px;
            }
        }

        p {
            margin: 10px 0 0 0;
            color: ${MUTED};
            font-size: 15px;
            line-height: 1.5;
            max-width: 60ch;
        }

        #btn-continue {
            display: none;

            @media screen and (min-width: 768px) {
                display: block;
                flex-shrink: 0;
                width: 200px;
                height: 60px;
            }
        }
    }

    .upsell-summary {
        display: flex;
        flex-wrap: wrap;
        background: ${CARD};
        border: 1px solid ${LINE};
        border-radius: 14px;
        padding: 4px 24px;
        margin-bottom: 36px;

        .item {
            flex: 1;
            min-width: 90px;
            padding: 18px 0;

            & + .item {
                border-left: 1px solid #f0f0f0;
                padding-left: 24px;
            }

            .k {
                margin-bottom: 6px;
                font-size: 12px;
                font-weight: 400;
                letter-spacing: 0.5px;
                text-transform: uppercase;
                color: ${MUTED};
            }

            .v {
                font-size: 20px;
                font-weight: 400;
                font-variant-numeric: tabular-nums;
            }
        }
    }

    #upsell-footer {
        position: fixed;
        left: 0;
        bottom: 0;
        width: 100%;
        z-index: 99;

        background-color: ${CARD};
        box-shadow: 0px -8px 30px -12px rgba(0, 0, 0, 0.18);
        border-top: 1px solid ${LINE};

        > .container {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
            padding-top: 14px;
            padding-bottom: 14px;

            > div:first-child {
                display: flex;
                align-items: center;
                font-size: 14px;
                font-weight: 600;

                > * {
                    line-height: normal;
                }

                > a {
                    /* Clear selection link */
                    display: flex;
                    align-items: center;
                    margin-left: 12px;
                    color: ${MUTED};
                    cursor: pointer;
                    transition: color 0.15s;

                    &:hover {
                        color: ${INK};
                    }

                    > i {
                        font-size: 20px;
                    }
                }
            }

            #btn-continue-footer {
                width: 140px;
                height: 56px;

                @media screen and (min-width: 768px) {
                    width: 200px;
                    height: 60px;
                }
            }
        }
    }
`;

type FPWProps = { isFloorPlanActive: boolean };

const FloorPlanWrapper = styled.div<FPWProps>`
    margin-bottom: 32px;

    h3 {
        margin: 0 0 14px 0;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: ${(props) => (props.isFloorPlanActive ? INK : MUTED)};
        transition: color 0.3s ease;

        &:empty {
            display: none;
        }
    }

    > ul {
        list-style-type: none;
        margin: 0;
        padding: 0;
        user-select: none;

        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;

        @media screen and (min-width: 768px) {
            grid-template-columns: repeat(2, 1fr);
        }

        opacity: ${(props) => (props.isFloorPlanActive ? 1 : 0.5)};
        transition: opacity 0.3s ease;
    }
`;

/**
 * Increments/decrements count value for menuId and returns a new list of IMenuSelect
 */
const doStepCount = (
    direction: 'Increment' | 'Decrement',
    menuId: number,
    guestsOnBooking: number,
    selections: MenuSelect[],
    allMenus: Menu[],
) => {
    const currentCount: number =
        selections.find((x) => x.id === menuId)?.count ?? 0;
    const menu = allMenus.find((x) => x.id === menuId)!;

    // Increment count value
    let nextCount = currentCount;
    if (direction === 'Increment') {
        nextCount = currentCount + 1;
    } else if (currentCount - 1 >= 0) {
        nextCount = currentCount - 1;
    }

    // Respect constraints
    if (nextCount < menu.minGuests) {
        if (direction === 'Increment') {
            nextCount = menu.minGuests;
        } else if (direction === 'Decrement') {
            nextCount = 0;
        }
    }

    if (nextCount > guestsOnBooking) {
        nextCount = currentCount;
    }

    const totalSelectedGuests = selections
        .filter((x) => x.id !== menuId)
        .map((x) => x.count)
        .reduce((a, b) => a + b, 0);
    if (totalSelectedGuests + nextCount > guestsOnBooking) {
        nextCount = currentCount;
    }

    if (menu.isExclusive) {
        if (direction === 'Increment') {
            nextCount = guestsOnBooking;
        } else if (direction === 'Decrement') {
            nextCount = 0;
        }
    }

    return selections.map((x) => ({
        id: x.id,
        count: x.id === menuId ? nextCount : x.count,
    }));
};

const filterOutConflicts: (
    selMenus: MenuSelect[],
    mId: number,
    allMenus: Menu[],
    guestsOnBooking: number,
) => MenuSelect[] = (selMenus, mId, allMenus, guestsOnBooking) => {
    const newMenu = allMenus.find((m) => m.id === mId)!;

    if (newMenu.isExclusive) {
        return [{ id: newMenu.id, count: guestsOnBooking }];
    } else {
        const menusOnSameFP = selMenus.filter(
            (sm) =>
                allMenus.filter(
                    (m) =>
                        m.id === sm.id &&
                        m.floorPlanIds.some((id) =>
                            newMenu.floorPlanIds.includes(id),
                        ),
                ).length,
        );

        const filteredSelections = selMenus.filter((sm) =>
            menusOnSameFP.map((m) => m.id).includes(sm.id),
        );
        if (filteredSelections.findIndex((f) => f.id === mId) !== -1) {
            return filteredSelections;
        } else {
            return [...filteredSelections, { id: mId, count: 0 }];
        }
    }
};

export interface MenuSelect {
    id: number;
    count: number;
}

const Upsell: FC<RouteComponentProps> = () => {
    const history = useHistory();
    const BookingProcessStore = useContext(BookingProcessStoreContext);

    const [upsell, setUpsell] = useState<UpsellAPINS.UpsellWithData | null>(
        null,
    );
    const [preselectedMenuId, setPreselectedMenuId] = useState<number | null>(
        null,
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const params: {
        restaurantId: string;
        dateTime: string;
        guests: string;
        eventId?: string;
        menuSelection?: string;
    } = queryString.parse(location.search);

    useEffect(() => {
        trackDineoutDiscovery('Reservation Step Viewed', { step: 'upsell' });
    }, []);
    const currentDateTime = moment(params.dateTime, moment.defaultFormatUtc);
    const restIdInt = parseInt(params.restaurantId, 10);
    const guestsInt = parseInt(params.guests, 10);
    const eventIdInt: number | null = params.eventId
        ? parseInt(params.eventId)
        : null;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const url = useMemo(() => new URLBuilder(history), []);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const {
        menuSelection: queryMenuSelection,
    }: { menuSelection: string | undefined } = queryString.parse(
        history.location.search,
    );
    const urlMenuSelections: MenuSelect[] = queryMenuSelection
        ? queryMenuSelection.split(',').map((x) => {
            const idCount = x.split(';');
            return {
                id: parseInt(idCount[0], 10),
                count: parseInt(idCount[1], 10),
            };
        })
        : [];
    const [selectedMenus, setSelectedMenus] =
        useState<MenuSelect[]>(urlMenuSelections);

    const { t, i18n } = useTranslation();

    /**
     * FETCH UPSELL DATA
     */
    useEffect(() => {
        const cancelToken = Axios.CancelToken.source();

        setUpsell(null);
        setSelectedMenus([]);

        getUpsell(
            restIdInt,
            params.dateTime,
            guestsInt,
            eventIdInt,
            cancelToken,
        )
            .then((res) => {
                setUpsell(res.data);

                // Check if we can preselect the menu for the user (skip selecting a menu if there's only one option available)
                const availableMenus = res.data.menus
                    .flatMap((x) => x.menus)
                    .filter((x) => x.isAvailable);
                const canPreselect = availableMenus.length === 1;
                if (canPreselect && !params.menuSelection) {
                    const availableMenu = availableMenus[0];
                    setPreselectedMenuId(availableMenu.id);
                    setSelectedMenus([
                        ...selectedMenus.filter(
                            (x) => x.id === availableMenu.id,
                        ),
                        { id: availableMenu.id, count: guestsInt },
                    ]);
                }
            })
            .catch(() => {
                alert('Error fetching data. Please try again.');
            });

        return () => cancelToken.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, params.dateTime]);

    /**
     * UPDATE QUERY PARAMS WHEN MENUS CHANGE
     */
    useEffect(() => {
        const setQueryParam = selectedMenus.some((m) => m.count > 0);
        if (!setQueryParam) {
            // Remove menuSelection from querystring if it already exists
            url.removeQueryParams(['menuSelection']);
            return;
        }

        const menuSelection = selectedMenus
            .filter((m) => m.count > 0)
            .map((m) => `${m.id};${m.count}`)
            .join(',');
        url.updateQueryParams({ menuSelection }, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMenus]);

    const updateSelected = useCallback(
        (direction: 'Increment' | 'Decrement', menuId: number) => {
            if (upsell) {
                const menus = upsell.menus
                    .map((x) => x.menus)
                    .reduce((a, b) => [...a, ...b], []);
                const filtered: MenuSelect[] = filterOutConflicts(
                    selectedMenus,
                    menuId,
                    menus,
                    guestsInt,
                );
                const incremented = doStepCount(
                    direction,
                    menuId,
                    guestsInt,
                    filtered,
                    menus,
                ).filter((x) => x.count > 0);
                setSelectedMenus(incremented);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [upsell, selectedMenus],
    );

    const totalSelectedGuests = useMemo(() => {
        return selectedMenus.map((x) => x.count).reduce((a, b) => a + b, 0);
    }, [selectedMenus]);

    const ALL_MENUS_ID = -9999;

    const currentFPId: number | undefined = useMemo(() => {
        if (upsell) {
            const menus = upsell.menus
                .map((x) => x.menus)
                .reduce((a, b) => [...a, ...b], []);
            if (selectedMenus.length > 0) {
                const menu = menus.find((m) => m.id === selectedMenus[0].id);
                if (menu && menu.floorPlanIds && menu.floorPlanIds.length > 1) {
                    return ALL_MENUS_ID;
                }
                return menu?.floorPlanIds?.[0];
            }
        }
        return undefined;
    }, [selectedMenus, upsell]);

    const multiFloorPlanMenus = useMemo(() => {
        if (!upsell) {
            return [];
        }

        const allMenus = upsell.menus
            .map((x) => x.menus)
            .reduce((a, b) => [...a, ...b], []);

        const seenMenuIds = new Set();

        return allMenus.filter((menu) => {
            if (seenMenuIds.has(menu.id)) {
                return false;
            }

            seenMenuIds.add(menu.id);
            return menu.floorPlanIds && menu.floorPlanIds.length > 1;
        });
    }, [upsell]);

    const prevFloorPlanId: number | undefined = usePrevious(currentFPId);

    const isContinueEnabled = useMemo(() => {
        if (upsell) {
            if (upsell.onlineMenuMandatory) {
                return totalSelectedGuests === guestsInt;
            }

            return true;
        }

        return false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upsell, selectedMenus]);

    const continueBooking = () => {
        BookingProcessStore.nextStep();
    };

    if (!upsell) {
        return <LoadingPage />;
    }

    const isFloorPlanSelected = (
        menu: any,
        floorPlanId: string | number,
    ): boolean => {
        if (menu.floorPlanIds.length > 1) {
            return floorPlanId === ALL_MENUS_ID;
        } else {
            return menu.floorPlanIds.includes(floorPlanId);
        }
    };

    const currency = currencies.get(upsell.currency);

    if (!currency) {
        return <div>Currency not found, Please contact Dineout support.</div>;
    }

    const displayedMenuIds = new Set();

    const multiFloorplanGroup = {
        floorPlanId: ALL_MENUS_ID,
        menus: multiFloorPlanMenus,
    };

    const allFpGroups = [multiFloorplanGroup, ...upsell.menus];

    return (
        <Style>
            <div className="upsell-top container">
                <HeaderOLD inverted />
            </div>
            <div className="container">
                <div className="upsell-header">
                    <div>
                        <h1>{t('pages.upsell.title')}</h1>
                        <p>
                            {upsell.onlineMenuMandatory
                                ? t('pages.upsell.subtitleRequired')
                                : t('pages.upsell.subtitleOptional')}
                        </p>
                    </div>
                    <div>
                        <Button
                            id="btn-continue"
                            onClick={() => continueBooking()}
                            disabled={!isContinueEnabled}
                        >
                            {t('pages.upsell.continue')}
                        </Button>
                    </div>
                </div>

                <div className="upsell-summary">
                    <div className="item">
                        <div className="k">{t('pages.complete.day')}</div>
                        <div className="v">
                            {currentDateTime.format('MMM DD')}
                        </div>
                    </div>
                    <div className="item">
                        <div className="k">{t('pages.complete.time')}</div>
                        <div className="v">
                            {currentDateTime.format('HH:mm')}
                        </div>
                    </div>
                    <div className="item">
                        <div className="k">{t('pages.complete.guests')}</div>
                        <div className="v">{params.guests}</div>
                    </div>
                </div>

                {allFpGroups.map((fpGroup, index) => {
                    if (index === 0) {
                        displayedMenuIds.clear();
                    }

                    const menusOnFloorPlan = fpGroup.menus
                        .filter((menu) => {
                            if (displayedMenuIds.has(menu.id)) {
                                return false;
                            }

                            if (
                                fpGroup.floorPlanId !== ALL_MENUS_ID &&
                                multiFloorPlanMenus.includes(menu)
                            ) {
                                return false;
                            }

                            displayedMenuIds.add(menu.id);
                            return true;
                        })

                        .map((m) => {
                            return (
                                <MenuCard
                                    menu={m}
                                    selectedMenus={selectedMenus}
                                    floorPlanId={fpGroup.floorPlanId}
                                    currentFPId={currentFPId}
                                    guests={guestsInt}
                                    updateSelected={updateSelected}
                                    totalSelectedGuests={totalSelectedGuests}
                                    isFloorPlanSelected={isFloorPlanSelected}
                                    prevFloorPlanId={prevFloorPlanId}
                                    currency={currency}
                                    locale={upsell.locale}
                                    preselectedMenuId={preselectedMenuId}
                                />
                            );
                        });

                    if (menusOnFloorPlan.length === 0) {
                        return null;
                    }

                    let isFPSelected = true;
                    if (selectedMenus.length > 0) {
                        const selectedMenuId = selectedMenus[0].id;
                        const selectedMenu = upsell.menus
                            .map((x) => x.menus)
                            .reduce((a, b) => [...a, ...b], [])
                            .find((x) => x.id === selectedMenuId);
                        if (selectedMenu) {
                            isFPSelected = isFloorPlanSelected(
                                selectedMenu,
                                fpGroup.floorPlanId,
                            );
                        }
                    }

                    return (
                        <FloorPlanWrapper
                            isFloorPlanActive={isFPSelected}
                            key={`fp-${fpGroup.floorPlanId}`}
                        >
                            <h3>
                                {fpGroup.floorPlanId === ALL_MENUS_ID
                                    ? ''
                                    : upsell.floorPlans.find(
                                        (x) => x.id === fpGroup.floorPlanId,
                                    )?.name}
                            </h3>
                            <ul>{menusOnFloorPlan}</ul>
                        </FloorPlanWrapper>
                    );
                })}
            </div>

            <div id="upsell-footer">
                <div className="container">
                    <div>
                        <div>{`${t(
                            'pages.upsell.selected',
                        )}: ${totalSelectedGuests}/${guestsInt} ${t(
                            'pages.upsell.guestsSelected',
                        )}`}</div>
                        <a
                            title={t('pages.upsell.clearSelectedGuests')}
                            onClick={() => {
                                setSelectedMenus([]);
                            }}
                            style={{
                                opacity: selectedMenus.length > 0 ? 1 : 0.5,
                            }}
                        >
                            <i className="material-icons">clear</i>
                        </a>
                    </div>
                    <Button
                        id="btn-continue-footer"
                        onClick={() => continueBooking()}
                        disabled={!isContinueEnabled}
                    >
                        {t('pages.upsell.continue')}
                    </Button>
                </div>
            </div>
        </Style>
    );
};

export default Upsell;
